import { useState, type FormEvent } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { CheckoutResponse } from '@brandonstrong/types';
import { api } from '../lib/api';
import { useSettings } from '../lib/useSettings';
import { PriceBreakdown } from '../components/PriceBreakdown';
import { Notice, Section } from '../components/Section';
import { clockTime, stayDate } from '../lib/format';

export function ConfirmationPage() {
  usePageMeta('Reservation confirmed', undefined, { noindex: true });
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const location = useLocation() as { state?: { email?: string; result?: CheckoutResponse } };
  const { data: settings } = useSettings();
  const [email, setEmail] = useState(location.state?.email ?? params.get('email') ?? '');
  const [lookupEmail, setLookupEmail] = useState(email);

  const lookup = useQuery({
    queryKey: ['reservation', code, lookupEmail],
    queryFn: () => api.reservation(code, lookupEmail),
    enabled: Boolean(code && lookupEmail),
    initialData: location.state?.result,
    retry: false,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setLookupEmail(email.trim());
  };

  const data = lookup.data;

  if (!data) {
    return (
      <Section narrow eyebrow="Reservation" title="Find your reservation">
        <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email used at booking"
            className="flex-1  border input-night"
          />
          <button type="submit" className="btn-primary">
            Look up {code}
          </button>
        </form>
        {lookup.isError && (
          <div className="mt-4">
            <Notice tone="danger">We could not find a reservation with that code and email.</Notice>
          </div>
        )}
      </Section>
    );
  }

  const r = data.reservation;
  const p = data.payment;

  return (
    <Section narrow eyebrow="Reservation" title="Your reservation">
      {/*
        Checkout closed on 2026-09-15, so nobody arrives here straight from a
        payment any more: everyone who reaches this page is looking up a room at
        the hotel that cancelled. The record is kept readable because the code is
        in their inbox and it is what they will quote when they ask about the
        refund - but the page has to lead with what happened to it.
      */}
      <div className="mb-8">
        <Notice tone="warning">
          <strong>This room is cancelled and is being refunded.</strong> The celebration moved to a
          new venue and the hotel block went with it. The refund goes back to the card that paid for
          it and there is nothing you need to do.{' '}
          <Link to="/update" className="text-pink-300 underline hover:text-pink-200">
            Read the family&apos;s update
          </Link>
          , or see{' '}
          <Link to="/rooms" className="text-pink-300 underline hover:text-pink-200">
            where to stay
          </Link>{' '}
          instead.
        </Notice>
      </div>
      <div className=" border border-pink-400/40 bg-night-900 p-6">
        <p className="text-sm text-ink-400">Confirmation code</p>
        <p className="font-display text-5xl tracking-wider text-pink-400">{r.confirmationCode}</p>
        <p className="mt-2 text-sm text-ink-400">Booked by {r.guest.email}.</p>

        <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ink-600">Room</dt>
            <dd className="text-ink-50">{r.roomType.name}</dd>
          </div>
          <div>
            <dt className="text-ink-600">Guest</dt>
            <dd className="text-ink-50">
              {r.guest.firstName} {r.guest.lastName}
            </dd>
          </div>
          <div>
            <dt className="text-ink-600">Check-in</dt>
            <dd className="text-ink-50">
              {stayDate(r.checkIn)}
              {settings ? ` from ${clockTime(settings.checkInTime)}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-ink-600">Check-out</dt>
            <dd className="text-ink-50">
              {stayDate(r.checkOut)}
              {settings ? ` by ${clockTime(settings.checkOutTime)}` : ''}
            </dd>
          </div>
          {p && (
            <div>
              <dt className="text-ink-600">Paid with</dt>
              <dd className="text-ink-50">
                {(p.cardBrand ?? 'Card').toUpperCase()} ending in {p.cardLast4}
              </dd>
            </div>
          )}
        </dl>

        <div className="mt-6">
          <PriceBreakdown
            price={{
              nights: r.nights,
              nightly: r.nightly,
              subtotal: r.subtotal,
              fees: r.fees,
              feeTotal: r.feeTotal,
              taxRate: r.taxRate,
              taxAmount: r.taxAmount,
              total: r.total,
              currency: 'USD',
            }}
          />
        </div>

        {/*
          The venue is deliberately not printed here any more. This record is a
          room at the hotel that cancelled, and putting the new venue's name and
          address beside it would read as though the room were there. The notice
          above the record says what actually happened to it.
        */}
        <p className="mt-6 text-sm text-ink-200">
          Coming to the celebration? Please{' '}
          <Link to="/rsvp" className="text-pink-300 underline hover:text-pink-200">
            RSVP
          </Link>{' '}
          with the names of your party: this reservation is no longer the door list.
        </p>
        <p className="mt-3 text-xs text-ink-600">
          Refunds go back to the card that paid and can take a few days to appear.
          {settings?.contactEmail ? ` Questions: ${settings.contactEmail}` : ''}
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button type="button" onClick={() => window.print()} className="btn-outline">
          Print
        </button>
        {settings?.donationUrl && (
          <a
            href={settings.donationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Donate to the family
          </a>
        )}
        <Link to="/event" className="btn-outline">
          Event details
        </Link>
      </div>
    </Section>
  );
}
